// Phase 2 — StatCard (Stripe-style). For Reports & Admin dashboards (docs/04 §4.3).
import * as React from "react";
import { type LucideIcon } from "lucide-react";
import { Card, CardContent } from "ui/card";
import { cn } from "lib/utils";

export interface StatCardProps {
  label: string;
  value: React.ReactNode;
  /** Small caption under the value, e.g. a delta or period. */
  caption?: React.ReactNode;
  /** "up" tints the caption success, "down" tints it destructive. */
  trend?: "up" | "down" | "neutral";
  icon?: LucideIcon;
  className?: string;
  /** Overrides the value text's default size/wrap classes. */
  valueClassName?: string;
}

export function StatCard({ label, value, caption, trend = "neutral", icon: Icon, className, valueClassName }: StatCardProps) {
  return (
    <Card className={cn("shadow-sm", className)}>
      <CardContent className="flex items-start justify-between gap-4 p-6">
        <div className="min-w-0 space-y-1">
          <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{label}</p>
          {/* No truncate — a KPI value getting silently clipped with "..." is
              worse than it wrapping onto a second line. */}
          <p className={cn("whitespace-normal break-words text-2xl font-semibold tabular-nums text-foreground", valueClassName)}>{value}</p>
          {caption && (
            <p
              className={cn(
                "text-xs",
                trend === "up" && "text-success",
                trend === "down" && "text-destructive",
                trend === "neutral" && "text-muted-foreground"
              )}
            >
              {caption}
            </p>
          )}
        </div>
        {Icon && (
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
            <Icon className="h-5 w-5" />
          </div>
        )}
      </CardContent>
    </Card>
  );
}
